import { Button } from "@shared/components/ui/Button";
import {
  Check,
  Download,
  FileSpreadsheet,
  Pencil,
  Plus,
  Search,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import React, { useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "react-hot-toast";
import { LeaderboardEntry } from "../../types/home.types";
import {
  buildEntries,
  detectColumns,
  downloadTemplate,
  EMAIL_PATTERN,
  exportEntries,
  isSamePerson,
  mergeEntries,
  parsePoints,
  ParsedSheet,
  rankEntries,
  readSpreadsheet,
  SheetColumns,
  SkippedRow,
} from "./leaderboard.utils";

export interface DraftEntry extends LeaderboardEntry {
  id: string;
}

let nextDraftId = 0;
export const toDraft = (entry: LeaderboardEntry): DraftEntry => ({
  ...entry,
  id: `entry-${nextDraftId++}`,
});

export const MAX_ENTRIES = 1000;
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const MAX_NAME_LENGTH = 80;

interface LeaderboardDataPanelProps {
  entries: DraftEntry[];
  onEntriesChange: (entries: DraftEntry[], sourceFileName?: string) => void;
  sourceFileName: string;
  hasUnsavedChanges: boolean;
  topN: number;
}

interface ImportReview {
  fileName: string;
  sheet: ParsedSheet;
  columns: SheetColumns;
}

interface ImportSummary {
  fileName: string;
  message: string;
  skipped: SkippedRow[];
}

interface RowForm {
  name: string;
  email: string;
  points: string;
}

const EMPTY_ROW: RowForm = { name: "", email: "", points: "" };

/** Returns the entry to store, or an error to show. */
function validateRow(
  form: RowForm,
  others: DraftEntry[],
): { entry: LeaderboardEntry } | { error: string } {
  const name = form.name.trim();
  const email = form.email.trim().toLowerCase();
  const points = parsePoints(form.points);

  if (!name) return { error: "Name is required." };
  if (name.length > MAX_NAME_LENGTH) return { error: `Name can have up to ${MAX_NAME_LENGTH} characters.` };
  if (email && !EMAIL_PATTERN.test(email)) return { error: "That email is not valid." };
  if (!Number.isFinite(points)) return { error: "Points must be a number." };

  const entry: LeaderboardEntry = email ? { name, email, points } : { name, points };
  const duplicate = others.find((other) => isSamePerson(other, entry));
  if (duplicate) {
    return { error: `${duplicate.name} is already on the list.` };
  }
  return { entry };
}

const iconButton =
  "p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:text-gray-200 dark:hover:bg-gray-700 transition-colors";

const LeaderboardDataPanel: React.FC<LeaderboardDataPanelProps> = ({
  entries,
  onEntriesChange,
  sourceFileName,
  hasUnsavedChanges,
  topN,
}) => {
  const [review, setReview] = useState<ImportReview | null>(null);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [newRow, setNewRow] = useState<RowForm>(EMPTY_ROW);
  const [newRowError, setNewRowError] = useState("");
  const [editing, setEditing] = useState<{ id: string; form: RowForm; error: string } | null>(null);
  const [search, setSearch] = useState("");
  const [confirmClear, setConfirmClear] = useState(false);

  const ranked = useMemo(
    () => rankEntries(entries).map((entry, position) => ({ ...entry, position })),
    [entries],
  );

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return ranked;
    return ranked.filter(
      (e) => e.name.toLowerCase().includes(term) || (e.email ?? "").includes(term),
    );
  }, [ranked, search]);

  const parsed = useMemo(
    () => (review ? buildEntries(review.sheet, review.columns) : null),
    [review],
  );

  const handleFile = async (file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      toast.error("The file is larger than 5 MB.");
      return;
    }

    try {
      const sheet = await readSpreadsheet(file);
      if (!sheet.rows.length || sheet.headers.length < 2) {
        toast.error("The file needs a header row and at least a name and a points column.");
        return;
      }
      setSummary(null);
      setReview({ fileName: file.name, sheet, columns: detectColumns(sheet) });
    } catch (error) {
      console.error("Error reading leaderboard file:", error);
      toast.error("That file could not be read. Use the Excel or CSV template.");
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (files) => files[0] && handleFile(files[0]),
    onDropRejected: () => toast.error("Only .xlsx, .xls or .csv files are allowed."),
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      "application/vnd.ms-excel": [".xls"],
      "text/csv": [".csv"],
    },
    maxFiles: 1,
    multiple: false,
  });

  const applyImport = (mode: "replace" | "merge") => {
    if (!review || !parsed) return;

    let message: string;
    if (mode === "replace") {
      onEntriesChange(parsed.entries.map(toDraft), review.fileName);
      message = `${parsed.entries.length} imported, the previous list was replaced`;
    } else {
      const result = mergeEntries(entries, parsed.entries, toDraft);
      onEntriesChange(result.entries, review.fileName);
      message = `${result.added} added, ${result.updated} updated`;
    }

    setSummary({ fileName: review.fileName, message, skipped: parsed.skipped });
    setReview(null);
    setEditing(null);
  };

  const setColumn = (key: keyof SheetColumns, value: string) => {
    setReview((current) => current && { ...current, columns: { ...current.columns, [key]: value } });
  };

  const handleAdd = () => {
    if (entries.length >= MAX_ENTRIES) {
      setNewRowError(`The leaderboard can have up to ${MAX_ENTRIES} people.`);
      return;
    }
    const result = validateRow(newRow, entries);
    if ("error" in result) {
      setNewRowError(result.error);
      return;
    }
    onEntriesChange([...entries, toDraft(result.entry)]);
    setNewRow(EMPTY_ROW);
    setNewRowError("");
  };

  const startEdit = (entry: DraftEntry) => {
    setEditing({
      id: entry.id,
      form: { name: entry.name, email: entry.email ?? "", points: String(entry.points) },
      error: "",
    });
  };

  const saveEdit = () => {
    if (!editing) return;
    const others = entries.filter((e) => e.id !== editing.id);
    const result = validateRow(editing.form, others);
    if ("error" in result) {
      setEditing({ ...editing, error: result.error });
      return;
    }
    onEntriesChange(entries.map((e) => (e.id === editing.id ? { ...result.entry, id: e.id } : e)));
    setEditing(null);
  };

  const handleDelete = (id: string) => {
    onEntriesChange(entries.filter((e) => e.id !== id));
    if (editing?.id === id) setEditing(null);
  };

  const handleClear = () => {
    onEntriesChange([], "");
    setConfirmClear(false);
    setEditing(null);
    setSummary(null);
  };

  // Enter inside these inputs would otherwise submit the whole modal form.
  const onRowKeyDown = (action: () => void, cancel?: () => void) => (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      action();
    } else if (event.key === "Escape" && cancel) {
      event.preventDefault();
      cancel();
    }
  };

  const headers = review?.sheet.headers ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-gray-600 dark:text-gray-300">Template:</span>
        <Button type="button" size="sm" variant="secondary" onClick={() => downloadTemplate("xlsx")}>
          <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
        </Button>
        <Button type="button" size="sm" variant="secondary" onClick={() => downloadTemplate("csv")}>
          <Download className="w-4 h-4 mr-1" /> CSV
        </Button>
      </div>

      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-5 text-center cursor-pointer transition-colors ${
          isDragActive
            ? "border-violet-500 bg-violet-50 dark:bg-violet-500/10"
            : "border-gray-300 dark:border-gray-600 hover:border-violet-500"
        }`}
      >
        <input {...getInputProps()} />
        <Upload className="w-6 h-6 mx-auto mb-2 text-gray-400" />
        <p className="text-gray-700 dark:text-gray-200 text-sm font-medium">
          Drop an Excel or CSV file here, or click to choose one
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Columns: Name, Email (optional) and Points. Sorted by points; ties share a place.
        </p>
      </div>

      {review && parsed && (
        <div className="rounded-lg border border-violet-200 dark:border-violet-500/40 bg-violet-50/60 dark:bg-violet-500/10 p-4 space-y-3">
          <div className="text-sm text-gray-700 dark:text-gray-200">
            <span className="font-medium">{review.fileName}</span>
            {" · "}
            {parsed.entries.length} valid {parsed.entries.length === 1 ? "row" : "rows"}
            {parsed.skipped.length > 0 && `, ${parsed.skipped.length} will be skipped`}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {(
              [
                { key: "name", label: "Name column", optional: false },
                { key: "email", label: "Email column", optional: true },
                { key: "points", label: "Points column", optional: false },
              ] as const
            ).map((column) => (
              <div key={column.key}>
                <label
                  htmlFor={`leaderboard-column-${column.key}`}
                  className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1"
                >
                  {column.label}
                </label>
                <select
                  id={`leaderboard-column-${column.key}`}
                  className="form-select w-full text-sm"
                  value={review.columns[column.key]}
                  onChange={(e) => setColumn(column.key, e.target.value)}
                >
                  {column.optional && <option value="">No email</option>}
                  {headers.map((header) => (
                    <option key={header} value={header}>
                      {header}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          <SkippedList skipped={parsed.skipped} />

          {parsed.entries.length === 0 ? (
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm text-red-600 dark:text-red-400">
                No valid rows with these columns.
              </p>
              <Button type="button" size="sm" variant="secondary" onClick={() => setReview(null)}>
                Cancel
              </Button>
            </div>
          ) : entries.length === 0 ? (
            <div className="flex justify-end gap-2">
              <Button type="button" size="sm" variant="secondary" onClick={() => setReview(null)}>
                Cancel
              </Button>
              <Button type="button" size="sm" onClick={() => applyImport("replace")}>
                Import {parsed.entries.length} {parsed.entries.length === 1 ? "person" : "people"}
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                The leaderboard already has {entries.length}{" "}
                {entries.length === 1 ? "person" : "people"}. Do you want to replace them all?
              </p>
              <div className="flex flex-wrap justify-end gap-2">
                <Button type="button" size="sm" variant="secondary" onClick={() => setReview(null)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => applyImport("merge")}
                  title="People already on the list get the points from the file; new ones are added"
                >
                  No, add to the list
                </Button>
                <Button type="button" size="sm" variant="danger" onClick={() => applyImport("replace")}>
                  Yes, replace all
                </Button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 text-right">
                Adding keeps everyone and updates the points of people already on the list
                (matched by email, or by name when there is no email).
              </p>
            </div>
          )}
        </div>
      )}

      {summary && (
        <div className="rounded-lg bg-gray-50 dark:bg-gray-700/40 p-3 text-sm text-gray-700 dark:text-gray-200 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div>
              <span className="font-medium">{summary.fileName}</span> · {summary.message}
              {summary.skipped.length > 0 && `, ${summary.skipped.length} skipped`}
            </div>
            <button type="button" className={iconButton} onClick={() => setSummary(null)} aria-label="Dismiss">
              <X className="w-4 h-4" />
            </button>
          </div>
          <SkippedList skipped={summary.skipped} />
        </div>
      )}

      <div>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <div className="text-sm text-gray-600 dark:text-gray-300">
            {entries.length} {entries.length === 1 ? "person" : "people"}
            {sourceFileName && ` · last file: ${sourceFileName}`}
            {hasUnsavedChanges && (
              <span className="ml-2 text-xs text-amber-600 dark:text-amber-400">Not saved yet</span>
            )}
          </div>
          {entries.length > 0 &&
            (confirmClear ? (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-700 dark:text-gray-200">Remove all {entries.length}?</span>
                <Button type="button" size="sm" variant="danger" onClick={handleClear}>
                  Yes, clear
                </Button>
                <Button type="button" size="sm" variant="secondary" onClick={() => setConfirmClear(false)}>
                  No
                </Button>
              </div>
            ) : (
              <div className="flex gap-1">
                <Button type="button" size="sm" variant="ghost" onClick={() => exportEntries(entries, "xlsx")}>
                  <Download className="w-4 h-4 mr-1" /> Excel
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => exportEntries(entries, "csv")}>
                  <Download className="w-4 h-4 mr-1" /> CSV
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => setConfirmClear(true)}>
                  <Trash2 className="w-4 h-4 mr-1" /> Clear
                </Button>
              </div>
            ))}
        </div>

        {/* Add a person by hand */}
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 mb-3">
          <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_100px_auto] gap-2">
            <input
              className="form-input w-full text-sm"
              placeholder="Name"
              aria-label="Name"
              maxLength={MAX_NAME_LENGTH}
              value={newRow.name}
              onChange={(e) => setNewRow({ ...newRow, name: e.target.value })}
              onKeyDown={onRowKeyDown(handleAdd)}
            />
            <input
              className="form-input w-full text-sm"
              placeholder="Email (optional)"
              aria-label="Email"
              type="email"
              value={newRow.email}
              onChange={(e) => setNewRow({ ...newRow, email: e.target.value })}
              onKeyDown={onRowKeyDown(handleAdd)}
            />
            <input
              className="form-input w-full text-sm text-right"
              placeholder="Points"
              aria-label="Points"
              inputMode="decimal"
              value={newRow.points}
              onChange={(e) => setNewRow({ ...newRow, points: e.target.value })}
              onKeyDown={onRowKeyDown(handleAdd)}
            />
            <Button type="button" size="sm" onClick={handleAdd}>
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
          {newRowError && <p className="text-xs text-red-600 dark:text-red-400 mt-1.5">{newRowError}</p>}
        </div>

        {entries.length > 10 && (
          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="search"
              className="form-input w-full pl-9 text-sm"
              placeholder="Search by name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={onRowKeyDown(() => undefined)}
            />
          </div>
        )}

        {entries.length > 0 && (
          <div className="max-h-80 overflow-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full text-sm table-fixed min-w-[520px]">
              <thead className="sticky top-0 z-[1] bg-gray-50 dark:bg-gray-700 text-xs uppercase text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="px-3 py-2 text-left w-12">#</th>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-left">Email</th>
                  <th className="px-3 py-2 text-right w-24">Points</th>
                  <th className="px-2 py-2 w-[76px]">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
                {visible.map((entry) =>
                  editing?.id === entry.id ? (
                    <tr key={entry.id} className="bg-violet-50/60 dark:bg-violet-500/10 align-top">
                      <td className="px-3 py-2 tabular-nums text-gray-500">{entry.rank}</td>
                      <td className="px-1 py-1.5">
                        <input
                          autoFocus
                          aria-label="Name"
                          className="form-input w-full py-1 text-sm"
                          maxLength={MAX_NAME_LENGTH}
                          value={editing.form.name}
                          onChange={(e) => setEditing({ ...editing, form: { ...editing.form, name: e.target.value } })}
                          onKeyDown={onRowKeyDown(saveEdit, () => setEditing(null))}
                        />
                        {editing.error && (
                          <p className="text-xs text-red-600 dark:text-red-400 mt-1">{editing.error}</p>
                        )}
                      </td>
                      <td className="px-1 py-1.5">
                        <input
                          aria-label="Email"
                          type="email"
                          className="form-input w-full py-1 text-sm"
                          value={editing.form.email}
                          onChange={(e) => setEditing({ ...editing, form: { ...editing.form, email: e.target.value } })}
                          onKeyDown={onRowKeyDown(saveEdit, () => setEditing(null))}
                        />
                      </td>
                      <td className="px-1 py-1.5">
                        <input
                          aria-label="Points"
                          inputMode="decimal"
                          className="form-input w-full py-1 text-sm text-right"
                          value={editing.form.points}
                          onChange={(e) => setEditing({ ...editing, form: { ...editing.form, points: e.target.value } })}
                          onKeyDown={onRowKeyDown(saveEdit, () => setEditing(null))}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="flex justify-end">
                          <button type="button" className={iconButton} onClick={saveEdit} aria-label="Save row" title="Save (Enter)">
                            <Check className="w-4 h-4 text-emerald-600" />
                          </button>
                          <button type="button" className={iconButton} onClick={() => setEditing(null)} aria-label="Cancel" title="Cancel (Esc)">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    <tr
                      key={entry.id}
                      className={entry.position >= topN ? "text-gray-400 dark:text-gray-500" : "text-gray-700 dark:text-gray-200"}
                    >
                      <td className="px-3 py-1.5 tabular-nums">{entry.rank}</td>
                      <td className="px-3 py-1.5 truncate" title={entry.name}>{entry.name}</td>
                      <td className="px-3 py-1.5 truncate" title={entry.email}>
                        {entry.email || <span className="text-gray-300 dark:text-gray-600">—</span>}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{entry.points.toLocaleString("en-US")}</td>
                      <td className="px-2 py-1">
                        <div className="flex justify-end">
                          <button type="button" className={iconButton} onClick={() => startEdit(entry)} aria-label={`Edit ${entry.name}`} title="Edit">
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button type="button" className={iconButton} onClick={() => handleDelete(entry.id)} aria-label={`Remove ${entry.name}`} title="Remove">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ),
                )}
                {visible.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-4 text-center text-gray-500 dark:text-gray-400">
                      Nobody matches this search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {entries.length > topN && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Greyed rows are saved but hidden: the home shows the top {topN}.
          </p>
        )}
        {entries.length > MAX_ENTRIES && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
            Only the top {MAX_ENTRIES} will be saved.
          </p>
        )}
      </div>
    </div>
  );
};

const SkippedList: React.FC<{ skipped: SkippedRow[] }> = ({ skipped }) =>
  skipped.length === 0 ? null : (
    <ul className="text-xs text-amber-700 dark:text-amber-400 space-y-0.5">
      {skipped.slice(0, 5).map((s) => (
        <li key={s.row}>
          Row {s.row}: {s.reason}
        </li>
      ))}
      {skipped.length > 5 && <li>…and {skipped.length - 5} more</li>}
    </ul>
  );

export default LeaderboardDataPanel;
